import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  FormControl,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  generateVideoQuestionsHelper,
} from "../../../../Ancillary/AIConfig/helper/ApiKeyHelper";
import { CustomButton } from "../../../../Base/views/CustomComponents";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const DEFAULT_TIME = 60; // seconds = 1 min
const STEP_INTERVAL = 15; // seconds
const MIN_TIME = 15; // optional safety
const MAX_TIME = 180; // 3 minutes

const VideoQuestionBuilder = ({
  videoQuestions,
  setVideoQuestions,
  jobPostData,
  setPostJobData,
  rolesresponsibilities = "",
  description= "",
}) => {
  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionTime, setNewQuestionTime] = useState(DEFAULT_TIME);

  const [loadingAI, setLoadingAI] = useState(false);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(videoQuestions);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setVideoQuestions(items);
  };

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return;

    setVideoQuestions((prev) => [
      ...prev,
      {
        id: Date.now(),
        question: newQuestion,
        time: formatTime(newQuestionTime),
        isAI: false,
      },
    ]);

    setNewQuestion("");
    setNewQuestionTime(DEFAULT_TIME);
  };

  const handleDelete = (index) => {
    setVideoQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  /* ---------------- AI GENERATE ---------------- */
  const handleGenerateAI = async () => {
    try {
      setLoadingAI(true);

      const job_details = {
        job_title: jobPostData?.title?.job_title,
        industry: jobPostData?.industry_type?.industry_name,
        skills: `${jobPostData?.primary_skill?.map((el) => el?.skill)?.join(", ")},${jobPostData?.secondary_skill?.map((el) => el?.skill)?.join(", ")}`,
        job_description: stripHtml(description),
        roles_responsibilities: stripHtml(rolesresponsibilities)
      };

      const aiResponse = await generateVideoQuestionsHelper({
        job_details: job_details
      });

      if (aiResponse && !aiResponse.hasError) {
        const videoQuestions = aiResponse.data?.videoQuestions;
        if (videoQuestions && videoQuestions.length) {
          const aiQuestions = videoQuestions.map((q, i) => ({
            id: Date.now() + i,
            question: q,
            time: "00:01:00",
            isAI: true,
          }));

          setVideoQuestions((prev) => [...prev, ...aiQuestions]);
        }
      }
    } catch (e) {
      console.error("AI video questions generation error", e);
    } finally {
      setLoadingAI(false);
    }
  };

  // update question time
  const updateNewQuestionTime = (direction) => {
    setNewQuestionTime((prev) => {
      if (direction === "inc") return Math.min(MAX_TIME, prev + STEP_INTERVAL);
      if (direction === "dec") return Math.max(MIN_TIME, prev - STEP_INTERVAL);
      return prev;
    });
  };

  const handleQuestionChange = (index, value) => {
    setVideoQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, question: value } : q)),
    );
  };
  const randomFreq = jobPostData?.prescreen_random_freq?.video_random_freq;
  return (
    <Grid container spacing={2}>
      {/* LEFT — QUESTION LIST */}
      <Grid item xs={12} md={6}>
        <Box
          border="1px solid #E4E7EC"
          borderRadius={2}
          p={2}
          bgcolor="#fff"
          display="flex"
          flexDirection="column"
          minHeight="260px"
          maxHeight="calc(100vh - 220px)"
        >
          {/* HEADER */}
          <Box mb={1}>
            <Typography fontWeight={600}>Additional Video Questions</Typography>
            <Grid container display={"flex"} alignItems={"flex-end"}>
              <Grid item xs={6}>
                <Typography fontSize={12} color="#667085">
                  {videoQuestions?.length} Questions
                </Typography>
                            
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    value={randomFreq || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if(value === "" || Number(value)) {
                        setPostJobData((prev) => ({
                          ...prev,
                          prescreen_random_freq: {
                            ...prev.prescreen_random_freq,
                            video_random_freq: value,
                          },
                        }));
                      }
                      // const total = videoQuestions?.length || 0;
                      // const errorMsg = validateRandomFrequency(value, total);
                      // !errorMsg && setError((prev) => ({
                      //   ...prev,
                      //   video_random_freq: "",
                      // }));
                    }}
                    variant="outlined"
                    fullWidth
                    label="Questions Frequency"
                    name="video_random_freq"
                    // error={!!error?.video_random_freq}
                    // helperText={error?.video_random_freq || ""}
                    InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title="Number of questions randomly selected from the video questions during candidate assessment."
                              arrow
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 18,
                                  color: "#98A2B3",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {/* DRAG LIST */}
          <Box flex={1} overflow="auto">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="videoQuestions">
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {!videoQuestions || videoQuestions?.length === 0 ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        color="#98A2B3"
                        mt={"10%"}
                      >
                        <VideocamOffIcon
                          sx={{ fontSize: 40, mb: 1, opacity: 0.6 }}
                        />

                        <Typography fontWeight={600}>
                          No video questions added
                        </Typography>

                        <Typography fontSize={13}>
                          Click "Add Your Question" {jobPostData?.is_ai_job && "or Generate using AI"} 
                        </Typography>
                      </Box>
                    ) : (
                      (videoQuestions || [])?.map((q, index) => (
                        <Draggable
                          key={q?.id}
                          draggableId={String(q?.id)}
                          index={index}
                        >
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                border: "1px solid #E4E7EC",
                                borderRadius: 3,
                                px: 2,
                                py: 1,
                                mb: 1,
                                mr: 0.5,
                                bgcolor: "#fff",
                              }}
                            >
                              {/* TOP ROW */}
                              <Box display="flex" alignItems={"flex-start"}>
                                <Box {...provided.dragHandleProps}>
                                  <DragIndicatorIcon
                                    sx={{ ml: -0.5, mr: 1, color: "#999" }}
                                  />
                                </Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  bgcolor="#EEF6FF"
                                  px={1}
                                  borderRadius={10}
                                >
                                  <Typography
                                    fontSize={13}
                                    fontWeight={600}
                                    color="#1976d2"
                                  >
                                    Question {index + 1}
                                  </Typography>
                                  {q?.isAI && (
                                    <AutoAwesomeIcon
                                      sx={{ fontSize: 16, color: "#1976d2" }}
                                    />
                                  )}
                                </Box>

                                <IconButton
                                  size="small"
                                  sx={{ ml: "auto" }}
                                  onClick={() => handleDelete(index)}
                                >
                                  <DeleteIcon color="#637381" />
                                </IconButton>
                              </Box>

                              {/* QUESTION TEXT + DELETE */}
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mt={0.5}
                              >
                                <TextField
                                  value={q?.question}
                                  onChange={(e) =>
                                    handleQuestionChange(index, e.target.value)
                                  }
                                  variant="outlined"
                                  fullWidth
                                  multiline
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      padding: "2px 8px",
                                    },
                                    "& textarea": {
                                      padding: "2px 0px",
                                    },
                                  }}
                                />
                              </Box>

                              {/* TIME */}
                              <Typography mt={0.5} fontWeight={500}>
                                {q?.time}
                              </Typography>
                            </Box>
                          )}
                        </Draggable>
                      ))
                    )}

                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Grid>

      {/* RIGHT — ADD PANEL */}

      <Grid item xs={12} md={6}>
        <Box
          border="1px solid #E4E7EC"
          borderRadius={2}
          p={2}
          bgcolor="#fff"
          minHeight="260px"
        >
          <Typography mb={1} fontWeight={600}>
            Video Question
          </Typography>

          <TextField
            fullWidth
            placeholder="Enter a question for video recording response"
            size="small"
            multiline
            maxRows={3}
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
          />

          {/* TIME DISPLAY */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              width: "fit-content",
              background: "#fff",
              mt: 1,
            }}
          >
            <Typography fontWeight={600}>
              {formatTime(newQuestionTime)}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <IconButton
                size="small"
                onClick={() => updateNewQuestionTime("inc")}
                disabled={newQuestionTime >= MAX_TIME}
                sx={{ p: 0.2 }}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => updateNewQuestionTime("dec")}
                sx={{ p: 0.2 }}
                disabled={newQuestionTime <= MIN_TIME}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* BUTTONS */}

          <Box
            mt={2}
            display="flex"
            flexDirection="column"
            gap={1}
            alignItems={"flex-end"}
          >
            <Button
              variant="contained"
              className="mpl-primary-btn"
              onClick={handleAddQuestion}
              sx={{ width: "200px" }}
            >
              + Add Your Question
            </Button>

          {jobPostData?.is_ai_job &&  <CustomButton
              variant="contained"
              onClick={handleGenerateAI}
              startIcon={<AutoAwesomeIcon />}
              className="ai-mpl-btn"
              sx={{ width: "200px" }}
              loading={loadingAI}
            >
              {loadingAI ? "Generating...." : "Generate Question"}
            </CustomButton>}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const formatTime = (totalSeconds) => {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
    2,
    "0",
  )}:${String(secs).padStart(2, "0")}`;
};

const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

export default VideoQuestionBuilder;
